import React from 'react';
import { Row, Col, Card, Form, Spinner, Badge, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faSave,
  faPaperPlane,
  faSearch,
  faMoneyCheckDollar,
  faCircleInfo,
  faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';

import ServerTable from '../../Components/Table/ServerTabe';
import { ConciliationApi, SapMasterApi } from '../../Services/ConciliationApiDataService';
import { formatCurrencyNum, formatDate, formatNumber } from '../../Utils/formaters';

const ActionBtn = ({ title, icon, onClick, disabled, variant = 'primary' }) => (
  <button className={`btn btn-${variant} fw-bold me-2`} onClick={onClick} disabled={disabled}>
    <FontAwesomeIcon icon={icon} className="me-1" />
    {title}
  </button>
);

const statusBadge = (status) => {
  const s = (status ?? '').toLowerCase();
  if (s === 'succeeded' || s === 'success') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'pending') return 'warning';
  if (s === 'skipped') return 'secondary';
  if (s === 'needsworkbench' || s === 'needsdata') return 'warning';
  if (s === 'completed') return 'success';
  if (s === 'entfinnomatch') return 'warning';
  if (s === 'erpnomatch') return 'secondary';
  if (s === 'match') return 'success';
  return 'secondary';
};

const decisionBadge = (decision) => {
  if (!decision) return <Badge bg="secondary">No decision</Badge>;
  if (decision.isCompleted || decision.IsCompleted) return <Badge bg="success">Completed</Badge>;
  return <Badge bg="warning" text="dark">Draft</Badge>;
};

const fieldRow = (label, value) => (
  <div className="ofnm-field-row">
    <div className="ofnm-field-label">{label}</div>
    <div className="ofnm-field-value">{value ?? <span className="text-muted">-</span>}</div>
  </div>
);

const findOption = (options, value) =>
  value ? options.find((o) => o.value === value) ?? null : null;

const firstValue = (obj, keys = []) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const normalizeDateInput = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const dateOnly = value.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : '';
  }
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const isTrue = (value) => {
  if (value === true) return true;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

const isInformationalStatus = (status) => (status ?? '').toUpperCase() === 'ENTFINNOMATCHREL';

const rowStatus = (row) => row?.estadoConcilia ?? row?.EstadoConcilia;

const isProcessedDetails = (details, workflow) => {
  if (isTrue(details?.isProcessed) || isTrue(details?.processed)) return true;
  if (isTrue(details?.IsProcessed)) return true;
  if (details?.sapDocEntry || details?.sapPaymentDocEntry || details?.paymentDocEntry) return true;
  if (details?.SapDocEntry) return true;
  return (workflow ?? []).some(
    (a) => (a.actionType === 'CreateVendorPayment' || a.actionType === 'ProcessConciliationPago') &&
      (a.status === 'Succeeded' || a.status === 'Success')
  );
};

const rowIsProcessed = (row) =>
  isTrue(row?.isProcessed) ||
  isTrue(row?.IsProcessed) ||
  isTrue(row?.processed) ||
  !!row?.sapDocEntry ||
  !!row?.sapPaymentDocEntry ||
  !!row?.paymentDocEntry ||
  !!row?.SapDocEntry;

const rowCanProcess = (row) => !rowIsProcessed(row) && !isInformationalStatus(rowStatus(row));

const EntFinNoMatchWorkbenchTab = ({ companyId, getToken, showInfoModal }) => {
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [selectedKeys, setSelectedKeys] = React.useState(new Set());
  const [selectedKey, setSelectedKey] = React.useState(null);

  const [details, setDetails] = React.useState(null);
  const [workflow, setWorkflow] = React.useState([]);

  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const [processingBatch, setProcessingBatch] = React.useState(false);
  const [loadingMasters, setLoadingMasters] = React.useState(false);

  const [vendorOptions, setVendorOptions] = React.useState([]);
  const [glOptions, setGlOptions] = React.useState([]);
  const [taOptions, setTaOptions] = React.useState([]);

  const [quickSearch, setQuickSearch] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState({});

  const [form, setForm] = React.useState({
    VendorCardCode: null,
    TransferAccount: null,
    DocCurrency: 'QTZ',
    TransferSum: '',
    TransferDate: '',
    TransferReference: '',
    CounterReference: '',
    Remarks: '',
    JournalRemarks: '',
    TargetInvoiceDocEntry: 0,
    SumApplied: ''
  });
  const activeCompanyRef = React.useRef(companyId);
  const detailsRequestSeqRef = React.useRef(0);

  React.useEffect(() => {
    activeCompanyRef.current = companyId;
  }, [companyId]);

  React.useEffect(() => {
    detailsRequestSeqRef.current += 1;
    setSelectedKey(null);
    setDetails(null);
    setWorkflow([]);
    setSelectedKeys(new Set());
    setLoadingDetails(false);
    setLoadingMasters(false);
    setVendorOptions([]);
    setGlOptions([]);
    setTaOptions([]);
  }, [companyId]);

  React.useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const requestCompanyId = companyId;

    (async () => {
      try {
        setLoadingMasters(true);
        const token = await getToken();
        const [vendors, gls] = await Promise.all([
          SapMasterApi.getVendors(companyId, { search: '', page: 1, pageSize: 2000 }, token),
          SapMasterApi.getGlAccounts(companyId, { search: '', page: 1, pageSize: 2000 }, token)
        ]);

        if (cancelled || activeCompanyRef.current !== requestCompanyId) return;

        setVendorOptions((vendors.items ?? []).map((v) => ({
          value: v.cardCode,
          label: `${v.cardCode} - ${v.cardName ?? ''}`
        })));

        setGlOptions((gls.items ?? []).map((a) => ({
          value: a.code,
          label: `${a.formatCode ?? a.code} - ${a.name ?? ''}`
        })));
        setTaOptions(
          (gls.items ?? [])
            .filter((a) => (a.cashAccout ?? a.cashAccount ?? a.CashAccount) === 'Y')
            .map((a) => ({
              value: a.code,
              label: `${a.formatCode ?? a.code} - ${a.name ?? ''}`
            }))
        );
      } catch (e) {
        console.error('Failed loading ENTFINNOMATCH masters', e);
      } finally {
        if (!cancelled && activeCompanyRef.current === requestCompanyId) setLoadingMasters(false);
      }
    })();

    return () => { cancelled = true; };
  }, [companyId, getToken]);

  const loadDetails = React.useCallback(async (conciliationKey) => {
    if (!conciliationKey) return;
    if (!activeCompanyRef.current) return;
    const requestId = detailsRequestSeqRef.current + 1;
    detailsRequestSeqRef.current = requestId;
    const requestCompanyId = activeCompanyRef.current;

    try {
      setLoadingDetails(true);
      const token = await getToken();

      const d = await ConciliationApi.getEntFinNoMatchWorkbench(conciliationKey, token);
      if (requestId !== detailsRequestSeqRef.current || activeCompanyRef.current !== requestCompanyId) return;

      setDetails(d);
      const embeddedActions = d?.actions ?? d?.Actions;
      if (embeddedActions) {
        setWorkflow(embeddedActions);
      } else {
        const actions = await ConciliationApi.getWorkflowActionsByKey(conciliationKey, token);
        if (requestId !== detailsRequestSeqRef.current || activeCompanyRef.current !== requestCompanyId) return;
        setWorkflow(actions ?? []);
      }

      const dec = d?.decision ?? d?.Decision ?? {};
      setForm({
        VendorCardCode: dec.VendorCardCode ?? dec.vendorCardCode ?? null,
        TransferAccount: dec.TransferAccount ?? dec.transferAccount ?? null,
        DocCurrency: dec.DocCurrency ?? dec.docCurrency ?? 'QTZ',
        TransferSum: dec.TransferSum ?? dec.transferSum ?? '',
        TransferDate: normalizeDateInput(dec.TransferDate ?? dec.transferDate),
        TransferReference: dec.TransferReference ?? dec.transferReference ?? '',
        CounterReference: dec.CounterReference ?? dec.counterReference ?? '',
        Remarks: dec.Remarks ?? dec.remarks ?? '',
        JournalRemarks: dec.JournalRemarks ?? dec.journalRemarks ?? '',
        TargetInvoiceDocEntry: dec.TargetInvoiceDocEntry ?? dec.targetInvoiceDocEntry ?? '',
        SumApplied: dec.SumApplied ?? dec.sumApplied ?? ''
      });
      setValidationErrors({});
    } catch (e) {
      if (requestId !== detailsRequestSeqRef.current || activeCompanyRef.current !== requestCompanyId) return;
      console.error('Failed loading ENTFINNOMATCH workbench details', e);
      showInfoModal('Error', <p className="mb-0">Could not load workbench details.</p>);
    } finally {
      if (requestId === detailsRequestSeqRef.current && activeCompanyRef.current === requestCompanyId) {
        setLoadingDetails(false);
      }
    }
  }, [getToken, showInfoModal]);

  const fetchEntFinRows = React.useCallback(async ({ pageIndex, pageSize, globalFilter, sorting, columnFilters }) => {
    if (!companyId) return { items: [], totalItems: 0 };
    const requestCompanyId = companyId;

    const sort = sorting?.[0];
    const sortByMap = {
      prioridad: 'PrioridadConcilia',
      pfFecha: 'PFFecha',
      pfNumeroTransaccion: 'PFNumeroTransaccion',
      pfMonto: 'PFMonto'
    };
    const sortBy = sortByMap[sort?.id] ?? 'PFFecha';
    const sortDir = sort ? (sort?.desc ? 'desc' : 'asc') : 'desc';
    const columnFilterMap = {
      prioridad: 'PrioridadConcilia',
      pfFecha: 'PFFecha',
      pfNumeroTransaccion: 'PFNumeroTransaccion',
      pfMonto: 'PFMonto'
    };
    const columnFilterParams = {};
    (columnFilters ?? []).forEach((f) => {
      const key = columnFilterMap[f?.id];
      const value = (f?.value ?? '').toString().trim();
      if (key && value) columnFilterParams[key] = value;
    });
    const search = globalFilter || quickSearch || '';

    const token = await getToken();
    const res = await ConciliationApi.getConciliationPagos({
      companyId: requestCompanyId,
      status: 'ENTFINNOMATCH',
      page: pageIndex + 1,
      pageSize,
      search,
      sortBy,
      sortDir,
      ...columnFilterParams
    }, token);
    if (activeCompanyRef.current !== requestCompanyId) {
      return { items: [], totalItems: 0 };
    }

    const items = res.items ?? [];
    setRows(items);

    return {
      items,
      totalItems: res.totalCount ?? res.totalItems ?? 0
    };
  }, [companyId, getToken, quickSearch, refreshTick]);

  const onSelectRow = async (row) => {
    const key = row.conciliationKey ?? row.ConciliationKey;
    setSelectedKey(key);
    await loadDetails(key);
  };

  const validateDecision = React.useCallback(() => {
    const errors = {};
    if (!form.TransferAccount) {
      errors.TransferAccount = 'TransferAccount is required for vendor payment.';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form.TransferAccount]);

  const saveDecision = async () => {
    if (!selectedKey) return;
    if (isProcessedDetails(details, workflow)) return;
    if (!validateDecision()) return;

    try {
      setSaving(true);
      const token = await getToken();
      await ConciliationApi.saveEntFinNoMatchDecision(selectedKey, form, token);
      await loadDetails(selectedKey);
      showInfoModal('Saved', <p className="mb-0">Decision saved successfully.</p>);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      console.error('Save decision failed', e);
      showInfoModal('Error', <p className="mb-0">Could not save decision.</p>);
    } finally {
      setSaving(false);
    }
  };

  const completeDecision = async () => {
    if (!selectedKey) return;
    if (isProcessedDetails(details, workflow)) return;
    if (!validateDecision()) return;

    try {
      setSaving(true);
      const token = await getToken();
      await ConciliationApi.completeEntFinNoMatchDecision(selectedKey, form, token);
      await loadDetails(selectedKey);
      showInfoModal('Completed', <p className="mb-0">Decision completed successfully.</p>);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      console.error('Complete decision failed', e);
      showInfoModal('Error', <p className="mb-0">Could not complete decision.</p>);
    } finally {
      setSaving(false);
    }
  };

  const processRecord = async () => {
    if (!selectedKey) return;

    const detailStatus = firstValue(details, ['estadoConcilia', 'EstadoConcilia']);
    if (isInformationalStatus(detailStatus)) {
      showInfoModal(
        'Informational status',
        <p className="mb-0">Status ENTFINNOMATCHREL is informational in this tab and cannot be processed here.</p>
      );
      return;
    }

    const processed = isProcessedDetails(details, workflow);
    if (processed) return;

    try {
      setProcessing(true);
      const token = await getToken();
      const res = await ConciliationApi.processConciliationPagoRecord(selectedKey, token);
      await loadDetails(selectedKey);
      setRefreshTick((t) => t + 1);

      showInfoModal(
        'Process result',
        <div>
          <p className="mb-2"><strong>Status:</strong> {res.status}</p>
          <p className="mb-2"><strong>Created Vendor Payment DocEntry:</strong> {firstValue(res, ['createdSapVendorPaymentDocEntry', 'CreatedSapVendorPaymentDocEntry', 'createdSapDocEntry', 'CreatedSapDocEntry']) ?? '-'}</p>
          {(res.messages ?? []).length > 0 && (
            <ul className="mb-0">
              {res.messages.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </div>
      );
    } catch (e) {
      console.error('Process failed', e);
      showInfoModal('Error', <p className="mb-0">Could not process record.</p>);
    } finally {
      setProcessing(false);
    }
  };

  const processSelected = async () => {
    const keys = rows
      .filter((r) => selectedKeys.has(r.conciliationKey ?? r.ConciliationKey) && rowCanProcess(r))
      .map((r) => r.conciliationKey ?? r.ConciliationKey);
    if (!keys.length) return;

    try {
      setProcessingBatch(true);

      showInfoModal(
        'Processing started',
        <p className="mb-0">
          The selected records are being processed now. This may take a moment depending on how many were selected.
        </p>
      );

      const token = await getToken();
      const res = await ConciliationApi.processConciliationPagoBatch(keys, token);

      setSelectedKeys(new Set());
      setRefreshTick((t) => t + 1);
      if (selectedKey) await loadDetails(selectedKey);

      showInfoModal(
        'Batch processing finished',
        <div>
          <p className="mb-2">
            Completed processing for <strong>{res.total}</strong> record(s).
          </p>
          <ul className="mb-2">
            <li>Succeeded: {res.succeeded}</li>
            <li>Failed: {res.failed}</li>
          </ul>
          {(res.items ?? []).some((x) => !x.success) && (
            <>
              <strong>Failures:</strong>
              <ul className="mb-0 mt-1">
                {res.items
                  .filter((x) => !x.success)
                  .slice(0, 10)
                  .map((x, i) => (
                    <li key={i}>
                      {x.conciliationKey} - {x.error ?? x.status}
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      );
    } catch (e) {
      console.error('Batch processing failed', e);
      showInfoModal('Error', <p className="mb-0">Could not process selected records.</p>);
    } finally {
      setProcessingBatch(false);
    }
  };

  const selectableRows = React.useMemo(
    () => rows.filter((r) => rowCanProcess(r)),
    [rows]
  );

  const columns = React.useMemo(() => [
    {
      id: 'select',
      header: () => (
        <Form.Check
          type="checkbox"
          checked={selectableRows.length > 0 && selectableRows.every((r) => selectedKeys.has(r.conciliationKey ?? r.ConciliationKey))}
          onChange={(e) => {
            const next = new Set(selectedKeys);
            if (e.target.checked) selectableRows.forEach((r) => next.add(r.conciliationKey ?? r.ConciliationKey));
            else selectableRows.forEach((r) => next.delete(r.conciliationKey ?? r.ConciliationKey));
            setSelectedKeys(next);
          }}
        />
      ),
      cell: ({ row }) => (
        <Form.Check
          type="checkbox"
          checked={selectedKeys.has(row.original.conciliationKey ?? row.original.ConciliationKey)}
          disabled={!rowCanProcess(row.original)}
          onChange={(e) => {
            const next = new Set(selectedKeys);
            if (e.target.checked) next.add(row.original.conciliationKey ?? row.original.ConciliationKey);
            else next.delete(row.original.conciliationKey ?? row.original.ConciliationKey);
            setSelectedKeys(next);
          }}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false
    },
    // {
    //   header: 'Status',
    //   id: 'estadoConcilia',
    //   cell: ({ row }) => {
    //     const status = row.original.estadoConcilia ?? row.original.EstadoConcilia;
    //     return <Badge bg={statusBadge(status)}>{status}</Badge>;
    //   }
    // },
    // {
    //   header: 'Decision',
    //   id: 'decisionStatus',
    //   enableSorting: false,
    //   enableColumnFilter: false,
    //   cell: ({ row }) =>
    //     (row.original.hasCompletedDecision ?? row.original.HasCompletedDecision)
    //       ? <Badge bg="success">Completed</Badge>
    //       : (row.original.hasDecision ?? row.original.HasDecision)
    //         ? <Badge bg="warning" text="dark">Draft</Badge>
    //         : <Badge bg="secondary">None</Badge>
    // },
    {
      header: 'Processed',
      id: 'processed',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        rowIsProcessed(row.original)
          ? <Badge bg="success">Yes</Badge>
          : <Badge bg="secondary">No</Badge>
      )
    },
    { header: 'Priority', id: 'prioridad', cell: ({ row }) => <div className="text-center">{row.original.prioridadConcilia ?? row.original.PrioridadConcilia}</div> },
    // { header: 'PE Date', id: 'peFecha', cell: ({ row }) => row.original.pEFecha ?? row.original.PEFecha },
    // { header: 'PE Tx Number', id: 'peNumeroTransaccion', cell: ({ row }) => row.original.pENumeroTransaccion ?? row.original.PENumeroTransaccion },
    // { header: 'PE Amount', id: 'peMonto', cell: ({ row }) => row.original.pEMonto ?? row.original.PEMonto },
    { header: 'Date', id: 'pfFecha', cell: ({row}) => formatDate(row.original.pfFecha) },
    { header: 'Tx Number', id: 'pfNumeroTransaccion', cell: ({ row }) => row.original.pfNumeroTransaccion ?? row.original.PFNumeroTransaccion },
    { header: 'Amount', id: 'pfMonto', cell: ({row}) => <div className="text-end">{formatCurrencyNum(row.original.pfMonto)}</div> },
  ], [selectableRows, selectedKeys, selectedKey]);

  const processed = isProcessedDetails(details, workflow);
  return (
    <div className="ofnm-tab-shell">
      <div className="ofnm-pane ofnm-left">
        <Card className="h-100 shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="fw-bold">
              <FontAwesomeIcon icon={faMoneyCheckDollar} className="me-2" />
              Transactions with no record on SAP
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <InputGroup size="sm" style={{ width: 260 }}>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search reference, amount, bank account..."
                  value={quickSearch}
                  onChange={(e) => setQuickSearch(e.target.value)}
                />
              </InputGroup>

              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setRefreshTick((t) => t + 1)}
                title="Refresh"
              >
                <FontAwesomeIcon icon={faRefresh} />
              </button>

              <button
                className="btn btn-sm btn-warning"
                onClick={processSelected}
                disabled={selectedKeys.size === 0 || processingBatch}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                Process Selected
              </button>
            </div>
          </Card.Header>

          <Card.Body className="ofnm-card-body p-0">
            <div className="ofnm-scroll">
              <ServerTable
                columns={columns}
                fetchData={fetchEntFinRows}
                tableClass="table-hover"
                containerClass="table-responsive text-nowrap"
                headerClass="table-light"
                enableGlobalFilter={false}
                initialPageSize={20}
                onRowDoubleClick={onSelectRow}
                getRowClassName={(row) => (selectedKey === (row.conciliationKey ?? row.ConciliationKey) ? 'table-primary' : '')}
              />
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="ofnm-pane ofnm-right">
        <Card className="h-100 shadow-sm position-relative">
          {processing && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white"
              style={{ zIndex: 10, opacity: 0.9 }}
            >
              <Spinner animation="border" className="mb-3" />
              <div className="fw-semibold">Processing in background...</div>
              <div className="text-muted small">Please wait while SAP posting completes.</div>
            </div>
          )}
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="fw-bold">
              <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
              Payment Workbench Details
            </div>

            <div className="d-flex flex-wrap gap-2">
              <ActionBtn
                title="Refresh"
                icon={faRefresh}
                variant="outline-primary"
                onClick={() => selectedKey && loadDetails(selectedKey)}
                disabled={!selectedKey || loadingDetails}
              />
              <ActionBtn
                title="Save"
                icon={faSave}
                onClick={saveDecision}
                disabled={!selectedKey || saving || processed}
              />
              <ActionBtn
                title="Complete"
                icon={faPaperPlane}
                onClick={completeDecision}
                disabled={!selectedKey || saving || processed}
              />
              <ActionBtn
                title="Process"
                icon={faPaperPlane}
                variant="warning"
                onClick={processRecord}
                disabled={!selectedKey || processing || processed}
              />
            </div>
          </Card.Header>

          <Card.Body className="ofnm-card-body">
            <div className="ofnm-scroll ofnm-details-scroll">
              {!selectedKey && (
                <div className="text-muted">
                  Double-click a row from the table to open the workbench.
                </div>
              )}

              {loadingDetails && (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" />
                </div>
              )}

              {!loadingDetails && details && (
                <>
                  {processed && (
                    <div className="alert alert-success mb-3">
                      This record has already been processed in SAP. The decision is now read-only.
                    </div>
                  )}

                  <div className="ofnm-summary-grid">
                    <Card className="shadow-sm">
                      <Card.Header className="fw-bold">Bank / Financial Entity</Card.Header>
                      <Card.Body>
                        {fieldRow('PE Empresa', firstValue(details, ['peEmpresaID', 'PEEmpresaID']))}
                        {fieldRow('PE Entidad Financiera', firstValue(details, ['peEntidadFinancieraID', 'PEEntidadFinancieraID']))}
                        {fieldRow('PE Cuenta', firstValue(details, ['peCuentaID', 'PECuentaID']))}
                        {fieldRow('PE Numero Transaccion', firstValue(details, ['peNumeroTransaccion', 'PENumeroTransaccion']))}
                        {fieldRow('PE Fecha', firstValue(details, ['peFecha', 'PEFecha']))}
                        {fieldRow('PE Monto', firstValue(details, ['peMonto', 'PEMonto']))}
                        {fieldRow('PF Empresa', firstValue(details, ['pfEmpresaID', 'PFEmpresaID']))}
                        {fieldRow('PF Entidad Financiera', firstValue(details, ['pFEntidadFinancieraID', 'PFEntidadFinancieraID']))}
                        {fieldRow('PF Cuenta', firstValue(details, ['pfCuentaID', 'PFCuentaID']))}
                        {fieldRow('PF Numero Transaccion', firstValue(details, ['pfNumeroTransaccion', 'PFNumeroTransaccion']))}
                        {fieldRow('PF Fecha', firstValue(details, ['pfFecha', 'PFFecha']))}
                        {fieldRow('PF Monto', firstValue(details, ['pfMonto', 'PFMonto']))}
                        {fieldRow('PF Llave', firstValue(details, ['pFllave', 'PFllave']))}
                        {fieldRow('Mov BankStatement Id', firstValue(details, ['movBankStatementId', 'MovBankStatementId']))}
                        {fieldRow('Mov BankEntry Id', firstValue(details, ['movBankEntryId', 'MovBankEntryId']))}
                        {fieldRow('Mov Booking Date', firstValue(details, ['movBookingDate', 'MovBookingDate']))}
                        {fieldRow('Mov Value Date', firstValue(details, ['movValueDate', 'MovValueDate']))}
                        {fieldRow('Mov Account Number', firstValue(details, ['movAccountNumber', 'MovAccountNumber']))}
                        {fieldRow('Mov Currency', firstValue(details, ['movCurrency', 'MovCurrency']))}
                        {fieldRow('Mov Currency Entry', firstValue(details, ['movCurrencyEntry', 'MovCurrencyEntry']))}
                        {fieldRow('Mov Amount', firstValue(details, ['movAmount', 'MovAmount']))}
                        {fieldRow('Mov D/C Indicator', firstValue(details, ['movDebitCreditIndicator', 'MovDebitCreditIndicator']))}
                        {fieldRow('Mov Entry Reference', firstValue(details, ['movEntryReference', 'MovEntryReference']))}
                        {fieldRow('Mov Additional Info', firstValue(details, ['movAdditionalInfo', 'MovAdditionalInfo']))}
                        {fieldRow('Mov Company Name', firstValue(details, ['movCompanyName', 'MovCompanyName']))}
                      </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                      <Card.Header className="fw-bold">ERP / Payment Context</Card.Header>
                      <Card.Body>
                        {fieldRow('Status', <Badge bg={statusBadge(firstValue(details, ['estadoConcilia', 'EstadoConcilia']))}>{firstValue(details, ['estadoConcilia', 'EstadoConcilia'])}</Badge>)}
                        {fieldRow('Priority', firstValue(details, ['prioridadConcilia', 'PrioridadConcilia']))}
                        {fieldRow('SAP DocEntry', firstValue(details, ['sapDocEntry', 'SapDocEntry']))}
                        {fieldRow('SAP Company Id', firstValue(details, ['sapCompanyId', 'SapCompanyId']))}
                        {fieldRow('SAP DocNum', firstValue(details, ['sapDocNum', 'SapDocNum']))}
                        {fieldRow('SAP DocDate', firstValue(details, ['sapDocDate', 'SapDocDate']))}
                        {fieldRow('SAP DocCurrency', firstValue(details, ['sapDocCurrency', 'SapDocCurrency']))}
                        {fieldRow('SAP CardCode', firstValue(details, ['sapCardCode', 'SapCardCode']))}
                        {fieldRow('SAP CardName', firstValue(details, ['sapCardName', 'SapCardName']))}
                        {fieldRow('SAP TransferAccount', firstValue(details, ['sapTransferAccount', 'SapTransferAccount']))}
                        {fieldRow('SAP TransferSum', firstValue(details, ['sapTransferSum', 'SapTransferSum']))}
                        {fieldRow('SAP TransferDate', firstValue(details, ['sapTransferDate', 'SapTransferDate']))}
                        {fieldRow('SAP TransferReference', firstValue(details, ['sapTransferReference', 'SapTransferReference']))}
                        {fieldRow('SAP CounterReference', firstValue(details, ['sapCounterReference', 'SapCounterReference']))}
                        {fieldRow('SAP Remarks', firstValue(details, ['sapRemarks', 'SapRemarks']))}
                        {fieldRow('SAP JournalRemarks', firstValue(details, ['sapJournalRemarks', 'SapJournalRemarks']))}
                        {fieldRow('Decision', decisionBadge(details.decision ?? details.Decision))}
                        {fieldRow('Processed', processed ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>)}
                      </Card.Body>
                    </Card>
                  </div>

                  {(details?.sapInvoices ?? details?.SapInvoices ?? []).length > 0 && (
                    <Card className="shadow-sm mt-3">
                      <Card.Header className="fw-bold">SAP Invoices</Card.Header>
                      <Card.Body>
                        {(details.sapInvoices ?? details.SapInvoices).map((inv, idx) => (
                          <div key={inv.docEntry ?? inv.DocEntry ?? idx} className="border rounded p-2 mb-2">
                            <div><strong>DocEntry:</strong> {inv.docEntry ?? inv.DocEntry}</div>
                            <div><strong>DocNum:</strong> {inv.docNum ?? inv.DocNum}</div>
                            <div><strong>DocTotal:</strong> {inv.docTotal ?? inv.DocTotal}</div>
                            <div><strong>OpenSum:</strong> {inv.openSum ?? inv.OpenSum}</div>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  )}

                  <Card className="shadow-sm mt-3">
                    <Card.Header className="fw-bold">Decision (Vendor Payment)</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={12} className="mb-3">
                          <Form.Label>VendorCardCode</Form.Label>
                          <Select
                            options={vendorOptions}
                            value={findOption(vendorOptions, form.VendorCardCode)}
                            onChange={(opt) => setForm((prev) => ({ ...prev, VendorCardCode: opt?.value ?? null }))}
                            isClearable
                            isDisabled={loadingMasters || processed}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </Col>

                        <Col md={12} className="mb-3">
                          <Form.Label>TransferAccount</Form.Label>
                          <Select
                            options={taOptions}
                            value={findOption(taOptions, form.TransferAccount)}
                            onChange={(opt) => {
                              setForm((prev) => ({ ...prev, TransferAccount: opt?.value ?? null }));
                              setValidationErrors((prev) => ({ ...prev, TransferAccount: null }));
                            }}
                            isClearable
                            isDisabled={loadingMasters || processed}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (base) => ({
                                ...base,
                                borderColor: validationErrors.TransferAccount ? '#dc3545' : base.borderColor
                              })
                            }}
                          />
                        </Col>
                        {validationErrors.TransferAccount && (
                          <Col md={12} className="mb-3">
                            <div className="text-danger small">{validationErrors.TransferAccount}</div>
                          </Col>
                        )}

                        <Col md={6} className="mb-3">
                          <Form.Label>DocCurrency</Form.Label>
                          <Form.Control
                            value={form.DocCurrency ?? ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, DocCurrency: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>TransferSum</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={form.TransferSum ?? ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, TransferSum: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>SumApplied</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={form.SumApplied ?? (details.pfMonto ?? 0.00)}
                            onChange={(e) => setForm((prev) => ({ ...prev, SumApplied: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>TransferDate</Form.Label>
                          <Form.Control
                            type="date"
                            value={form.TransferDate ?? ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, TransferDate: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>TransferReference</Form.Label>
                          <Form.Control
                            value={form.TransferReference ?? 'Transfer Ref'}
                            onChange={(e) => setForm((prev) => ({ ...prev, TransferReference: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>CounterReference</Form.Label>
                          <Form.Control
                            value={form.CounterReference ?? 'Recibo'}
                            onChange={(e) => setForm((prev) => ({ ...prev, CounterReference: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>

                        <Col md={6} className="mb-3">
                          <Form.Label>TargetInvoiceDocEntry</Form.Label>
                          <Form.Control
                            type='number'
                            step='1'
                            value={form.TargetInvoiceDocEntry ?? 0}
                            onChange={(e) => setForm((prev) => ({ ...prev, TargetInvoiceDocEntry: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>

                        <Col md={12} className="mb-3">
                          <Form.Label>Remarks</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={form.Remarks ?? ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, Remarks: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>

                        <Col md={12} className="mb-2">
                          <Form.Label>JournalRemarks</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={form.JournalRemarks ?? ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, JournalRemarks: e.target.value }))}
                            disabled={processed}
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Card className="shadow-sm mt-3">
                    <Card.Header className="fw-bold">
                      <FontAwesomeIcon icon={faClockRotateLeft} className="me-2" />
                      Workflow History
                    </Card.Header>
                    <Card.Body>
                      {(workflow ?? []).length === 0 && (
                        <div className="text-muted">No actions yet.</div>
                      )}

                      {(workflow ?? []).map((a, idx) => (
                        <div key={a.actionId ?? a.ActionId ?? idx} className="border rounded p-2 mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <strong>{a.actionType ?? a.ActionType}</strong>
                            <Badge bg={statusBadge(a.status ?? a.Status)}>{a.status ?? a.Status}</Badge>
                          </div>
                          <div className="small text-muted mt-1">
                            Attempts: {a.attemptCount ?? a.AttemptCount} {(a.lastAttemptAtUtc ?? a.LastAttemptAtUtc) ? `| Last: ${a.lastAttemptAtUtc ?? a.LastAttemptAtUtc}` : ''}
                          </div>
                          {(a.externalRef1 ?? a.ExternalRef1) && <div className="small mt-1">Ref1: {a.externalRef1 ?? a.ExternalRef1}</div>}
                          {(a.externalRef2 ?? a.ExternalRef2) && <div className="small">Ref2: {a.externalRef2 ?? a.ExternalRef2}</div>}
                          {(a.error ?? a.Error) && <div className="small text-danger mt-1">{a.error ?? a.Error}</div>}
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default EntFinNoMatchWorkbenchTab;
